import { Link } from 'react-router-dom';

const deepFeatures = [
  {
    tag: 'Core Feature #1',
    title: 'AI-Powered Ad Copywriting That Actually Converts',
    body: 'Most AI writing tools generate generic content. AdForge\'s AI ad generator is trained specifically on high-performing advertising — it understands hooks, emotional triggers, platform tone differences, and conversion psychology. Every ad uses a proven framework: Hook → Story → Offer → CTA.',
    bullets: [
      'Writes for 5 platforms: Facebook, Instagram, Google, TikTok, LinkedIn',
      'Generates multiple ad angles: FOMO, social proof, problem/solution, authority',
      'Matches your brand voice and target audience persona',
      'Up to 20 variations per generation for A/B testing',
      'Respects platform character limits automatically',
    ],
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/86e896e6-9422-4b62-9ef3-6c1eb29ac17f_ChatGPT-Image-Mar-27-2026-11_04_16-AM.png?v=f500ff18b008dec18cf2f07ce0147fe9',
    imageAlt: 'AI ad copywriting feature showing multiple ad variations',
    flip: false,
  },
  {
    tag: 'Core Feature #2',
    title: 'AI-Generated Ad Images — No Designer Needed',
    body: 'Every ad generation includes a custom AI-produced creative image tailored to your business, offer, and platform. Forget stock photo libraries — your ads get unique visuals that no competitor will have. Images are generated at the correct aspect ratio for each platform automatically.',
    bullets: [
      'Custom AI images per generation — never the same twice',
      'Correct dimensions for every platform (1:1, 16:9, 9:16, 1.91:1)',
      'Regenerate individual images without re-writing the copy',
      'Edit images in the built-in visual editor',
      'Free plan: 1 image/gen · Pro: 3 images · Agency: 6 images',
    ],
    image: 'https://storage.readdy-site.link/project_files/b82d3669-b3d5-44e8-8c89-634acf8d0274/73e5d060-6112-4c83-b443-3548c02be6fa_ChatGPT-Image-Mar-27-2026-11_00_35-AM.png?v=4ba7e6ce38d30896dc2a1db6d340583d',
    imageAlt: 'AI generated ad images feature grid',
    flip: true,
  },
  {
    tag: 'Core Feature #3',
    title: 'Visual Ad Editor — Customize Everything Without Code',
    body: 'Once your AI-generated ad is ready, open it in AdForge\'s built-in visual editor. Drag and drop elements, edit text inline, swap colors, resize layers, and perfect your creative before exporting. It\'s the creative polish layer that turns a great AI draft into a pixel-perfect final ad.',
    bullets: [
      'Drag-and-drop canvas editor — no design experience needed',
      'Edit text, images, buttons, and backgrounds independently',
      'Layer management with lock/hide controls',
      'Export at any resolution — print-ready or web-optimized',
      'Undo/redo, alignment guides, and snap-to-grid',
    ],
    image: 'https://readdy.ai/api/search-image?query=modern%20drag%20and%20drop%20visual%20editor%20interface%20shown%20on%20a%20wide%20monitor%20with%20design%20panels%20toolbars%20and%20a%20colorful%20ad%20creative%20on%20the%20canvas%2C%20professional%20UX%20design%20aesthetic%2C%20clean%20workspace%2C%20amber%20accent%20colors%2C%20sharp%20high%20resolution%20screen&width=720&height=480&seq=deepdive-editor-01&orientation=landscape',
    imageAlt: 'Visual ad editor interface for customizing AI generated ads',
    flip: false,
  },
];

export default function FeaturesDeepDive() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">Deep Dive</span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            The Three Core Features That Set AdForge Apart
          </h2>
          <p className="text-stone-500 text-base leading-relaxed">
            Other tools do one thing. AdForge combines copy, creative, and editing — the complete
            pipeline for producing professional ads with AI, from idea to launch.
          </p>
        </div>

        <div className="space-y-16">
          {deepFeatures.map((feat) => (
            <article
              key={feat.tag}
              className={`flex flex-col ${feat.flip ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}
            >
              {/* Image */}
              <div className="w-full lg:w-1/2 rounded-2xl overflow-hidden border border-stone-100 flex-shrink-0 h-64 lg:h-80">
                <img
                  src={feat.image}
                  alt={feat.imageAlt}
                  title={feat.imageAlt}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Content */}
              <div className="flex-1">
                <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-2 block">
                  {feat.tag}
                </span>
                <h3 className="text-2xl font-black text-stone-900 mb-4 leading-snug">
                  {feat.title}
                </h3>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">{feat.body}</p>
                <ul className="space-y-2.5">
                  {feat.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-orange-500 text-sm" />
                      </div>
                      <span className="text-sm text-stone-600">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Unlock All Features Free
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
